import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { QueryScope, PanelDataMap, PanelFilter } from '../interfaces/panel-data';
import { PanelDataService } from '../model/PanelDataService';
import { buildPanelFilters, serializePanelFilters } from '../utils/panel-filters';

export interface UsePanelDataOptions {
  /** Extra panel-specific filters, merged with those derived from the scope and time window. */
  filters?: PanelFilter[];
  enabled?: boolean;
  timeWindow?: string;
}

const panelDataService = new PanelDataService();

export function usePanelData<T extends keyof PanelDataMap>(
  panelType: T,
  scope: QueryScope,
  options?: UsePanelDataOptions
): UseQueryResult<PanelDataMap[T]> {
  const filterParams = serializePanelFilters(buildPanelFilters(scope, options));

  return useQuery({
    // Keyed on the serialised filters rather than on the individual inputs, so the key is exactly
    // what the request is: two calls that resolve to the same filters share a cache entry.
    queryKey: ['panel-data', panelType, scope.type, filterParams],
    queryFn: async () => {
      const response = await panelDataService.queryPanelData({
        panelType,
        scopeType: scope.type,
        filterParams,
      });
      // A malformed payload is not validated here: the panels guard themselves with an
      // ErrorBoundary, which catches every render-time failure rather than only the fields a
      // validator thought to check.
      return JSON.parse(response.data) as PanelDataMap[T];
    },
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
  });
}
